import { supabase } from './supabase';
import type { TeamAverageScores } from '../types/team';
import type { Score } from '../types';
import type { HarmonicScore } from '../types/harmonic';

export interface TeamMetrics {
  leadershipReadiness: number;
  innovationCapacity: number;
  collaborationStrength: number;
  resilienceIndex: number;
  executionDrive: number;
  growthOrientation: number;
  emotionalStability: number;
  engagementRisk: number;
  frictionWithdrawal: number;
  frictionFear: number;
  frictionConflict: number;
  teamBalance: number;
  strengthsConcentration: number;
  momentum: number;
}

export interface MetricDefinition {
  key: keyof TeamMetrics;
  label: string;
  description: string;
  category: 'capability' | 'harmonic' | 'distribution';
  higherIsBetter: boolean;
}

export const metricDefinitions: MetricDefinition[] = [
  {
    key: 'leadershipReadiness',
    label: 'Leadership Readiness',
    description: 'Capacity to take initiative, make decisions, and drive direction',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'innovationCapacity',
    label: 'Innovation Capacity',
    description: 'Collective ability to generate and act on new ideas',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'collaborationStrength',
    label: 'Collaboration Strength',
    description: 'Natural ability to connect, communicate, and maintain trust',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'resilienceIndex',
    label: 'Resilience Index',
    description: 'Ability to bounce back from setbacks and maintain momentum under stress',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'executionDrive',
    label: 'Execution Drive',
    description: 'Collective bias toward action and follow-through',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'growthOrientation',
    label: 'Growth Orientation',
    description: 'Hunger for development and ability to absorb new skills',
    category: 'capability',
    higherIsBetter: true,
  },
  {
    key: 'emotionalStability',
    label: 'Emotional Stability',
    description: 'Balance between constructive and destructive emotional states',
    category: 'harmonic',
    higherIsBetter: true,
  },
  {
    key: 'engagementRisk',
    label: 'Engagement Risk',
    description: 'Proportion of team in disengaged emotional states',
    category: 'harmonic',
    higherIsBetter: false,
  },
  {
    key: 'frictionWithdrawal',
    label: 'Friction: Withdrawal',
    description: 'People pulling away (apathy, hopelessness, grief)',
    category: 'harmonic',
    higherIsBetter: false,
  },
  {
    key: 'frictionFear',
    label: 'Friction: Fear-based',
    description: 'People hiding or avoiding (fear, passive hostility)',
    category: 'harmonic',
    higherIsBetter: false,
  },
  {
    key: 'frictionConflict',
    label: 'Friction: Conflict',
    description: 'People pushing against (open anger, antagonism)',
    category: 'harmonic',
    higherIsBetter: false,
  },
  {
    key: 'teamBalance',
    label: 'Team Balance',
    description: 'Diversity of strengths across team members (higher = more diverse)',
    category: 'distribution',
    higherIsBetter: true,
  },
  {
    key: 'strengthsConcentration',
    label: 'Strengths Concentration',
    description: 'How well-distributed high-performers are across traits',
    category: 'distribution',
    higherIsBetter: true,
  },
  {
    key: 'momentum',
    label: 'Momentum',
    description: 'Forward energy and acceleration beyond baseline stability',
    category: 'harmonic',
    higherIsBetter: true,
  },
];

function getCharScore(scores: Score[], id: string): number {
  return scores.find(s => s.characteristicId === id)?.normalized || 0;
}

function getHarmScore(scores: HarmonicScore[], levelId: number): number {
  return scores.find(s => s.levelId === levelId)?.normalized || 0;
}

function avg(...values: number[]): number {
  if (values.length === 0) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

export function computeTeamMetricsFromAverages(averages: TeamAverageScores): TeamMetrics {
  const s = averages.averageScores;
  const h = averages.averageHarmonicScores;

  const ambition = getCharScore(s, 'ambition');
  const boldness = getCharScore(s, 'boldness');
  const integrity = getCharScore(s, 'integrity');
  const positiveOutlook = getCharScore(s, 'positiveOutlook');
  const passion = getCharScore(s, 'passion');
  const sociability = getCharScore(s, 'sociability');
  const communication = getCharScore(s, 'communication');
  const wisdom = getCharScore(s, 'wisdom');
  const learning = getCharScore(s, 'learning');
  const creativity = getCharScore(s, 'creativity');
  const flexibility = getCharScore(s, 'flexibility');
  const visionaryThinking = getCharScore(s, 'visionaryThinking');

  const leadershipReadiness = avg(ambition, boldness, visionaryThinking, wisdom);
  const innovationCapacity = avg(creativity, visionaryThinking, boldness, flexibility);
  const collaborationStrength = avg(sociability, communication, positiveOutlook, integrity);
  const resilienceIndex = avg(flexibility, positiveOutlook, passion, ambition);
  const executionDrive = avg(ambition, passion, boldness, communication);
  const growthOrientation = avg(learning, creativity, flexibility, visionaryThinking);

  // Harmonic-derived metrics
  const upperSum = getHarmScore(h, 9) + getHarmScore(h, 10) + getHarmScore(h, 11) + getHarmScore(h, 12);
  const lowerSum = getHarmScore(h, 1) + getHarmScore(h, 2) + getHarmScore(h, 3) + getHarmScore(h, 4) + getHarmScore(h, 5);
  const totalHarmRange = upperSum + lowerSum || 1;
  const emotionalStability = Math.round((upperSum / totalHarmRange) * 100);

  // Engagement risk: apathy (1), hopelessness (2), rational neutrality (8) -- disengaged states
  const engagementRisk = avg(getHarmScore(h, 1), getHarmScore(h, 2), getHarmScore(h, 8));

  // Friction breakdown
  const frictionWithdrawal = avg(getHarmScore(h, 1), getHarmScore(h, 2), getHarmScore(h, 3));
  const frictionFear = avg(getHarmScore(h, 4), getHarmScore(h, 5));
  const frictionConflict = avg(getHarmScore(h, 6), getHarmScore(h, 7));

  // Momentum: weighted toward enthusiasm and exhilaration
  const momentum = Math.round(
    (getHarmScore(h, 9) * 1.0 + getHarmScore(h, 10) * 1.2 + getHarmScore(h, 11) * 1.5 + getHarmScore(h, 12) * 2.0) /
    5.7
  );

  return {
    leadershipReadiness,
    innovationCapacity,
    collaborationStrength,
    resilienceIndex,
    executionDrive,
    growthOrientation,
    emotionalStability,
    engagementRisk,
    frictionWithdrawal,
    frictionFear,
    frictionConflict,
    teamBalance: 0,
    strengthsConcentration: 0,
    momentum,
  };
}

export function computeDistributionMetrics(
  memberResults: { scores: Score[] }[]
): { teamBalance: number; strengthsConcentration: number } {
  if (memberResults.length < 2) {
    return { teamBalance: 50, strengthsConcentration: 50 };
  }

  const charIds = [
    'ambition', 'boldness', 'integrity', 'positiveOutlook',
    'passion', 'sociability', 'communication', 'wisdom',
    'learning', 'creativity', 'flexibility', 'visionaryThinking',
  ];

  // Team Balance: average standard deviation across characteristics (normalized to 0-100)
  let totalStdDev = 0;
  charIds.forEach(charId => {
    const values = memberResults.map(r => getCharScore(r.scores, charId));
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
    totalStdDev += Math.sqrt(variance);
  });
  const avgStdDev = totalStdDev / charIds.length;
  // Normalize: stddev of 25 = "maximally diverse" (100), 0 = no diversity (0)
  const teamBalance = Math.min(100, Math.round((avgStdDev / 25) * 100));

  // Strengths Concentration: how many characteristics have multiple high scorers (>75)
  let totalHighScorers = 0;
  let charsWithMultipleHigh = 0;
  charIds.forEach(charId => {
    const highCount = memberResults.filter(r => getCharScore(r.scores, charId) >= 75).length;
    totalHighScorers += highCount;
    if (highCount >= 2) charsWithMultipleHigh++;
  });
  // Normalize: if all 12 chars have multiple high scorers = 100
  const strengthsConcentration = Math.round((charsWithMultipleHigh / charIds.length) * 100);

  return { teamBalance, strengthsConcentration };
}

export function computeFullTeamMetrics(
  averages: TeamAverageScores,
  memberResults: { scores: Score[] }[]
): TeamMetrics {
  const metrics = computeTeamMetricsFromAverages(averages);
  const distribution = computeDistributionMetrics(memberResults);
  metrics.teamBalance = distribution.teamBalance;
  metrics.strengthsConcentration = distribution.strengthsConcentration;
  return metrics;
}

// Trend tracking
export interface InsightSnapshot {
  snapshotDate: string;
  memberCount: number;
  membersWithResults: number;
  metrics: TeamMetrics;
}

export async function loadInsightSnapshots(teamId: string): Promise<InsightSnapshot[]> {
  const { data, error } = await (supabase as any)
    .from('team_insights_snapshots')
    .select('snapshot_date, member_count, members_with_results, metrics')
    .eq('team_id', teamId)
    .order('snapshot_date', { ascending: true });

  if (error) throw error;

  return (data || []).map((row: any) => ({
    snapshotDate: row.snapshot_date,
    memberCount: row.member_count,
    membersWithResults: row.members_with_results,
    metrics: row.metrics as TeamMetrics,
  }));
}

export async function saveInsightSnapshot(
  teamId: string,
  memberCount: number,
  membersWithResults: number,
  metrics: TeamMetrics
): Promise<void> {
  const { error } = await (supabase as any)
    .from('team_insights_snapshots')
    .upsert(
      {
        team_id: teamId,
        snapshot_date: new Date().toISOString().split('T')[0],
        member_count: memberCount,
        members_with_results: membersWithResults,
        metrics,
      },
      { onConflict: 'team_id,snapshot_date' }
    );

  if (error) throw error;
}

export function getMetricLevel(value: number, higherIsBetter: boolean): { label: string; color: string } {
  if (higherIsBetter) {
    if (value <= 30) return { label: 'Low', color: 'text-red-600' };
    if (value <= 55) return { label: 'Developing', color: 'text-amber-600' };
    if (value <= 75) return { label: 'Strong', color: 'text-emerald-600' };
    return { label: 'Exceptional', color: 'text-emerald-700' };
  } else {
    if (value <= 20) return { label: 'Minimal', color: 'text-emerald-600' };
    if (value <= 40) return { label: 'Moderate', color: 'text-amber-600' };
    if (value <= 60) return { label: 'Elevated', color: 'text-orange-600' };
    return { label: 'Critical', color: 'text-red-600' };
  }
}
